import { Canvas } from 'fabric'
import { calculateCanvasDimensions } from '../../utils/canvas/canvasDimensions'
import { CANVAS, MIN_FONT_SIZE } from '../../constants'
import type { CanvasInitParams, MediaDimensions } from '../types'

/**
 * Определяет оптимальное качество рендеринга для canvas в зависимости от размера шрифта
 * Для маленьких шрифтов (4-6px) используем более резкие настройки для лучшей читаемости
 * Для больших шрифтов используем высокое качество для плавности
 * @param fontSize - размер шрифта текста на canvas
 * @returns настройки качества рендеринга
 */
function getOptimalRenderingSettings(fontSize: number): {
    imageSmoothingEnabled: boolean
    imageSmoothingQuality: 'low' | 'medium' | 'high'
} {
    // Для очень маленьких шрифтов (4-5px) отключаем сглаживание для максимальной четкости
    if (fontSize <= 5) {
        return {
            imageSmoothingEnabled: false, // Отключаем сглаживание для резкости
            imageSmoothingQuality: 'low',
        }
    }

    // Для маленьких шрифтов (6px) используем низкое качество сглаживания
    if (fontSize <= 6) {
        return {
            imageSmoothingEnabled: true,
            imageSmoothingQuality: 'low', // Низкое качество = более резкое
        }
    }

    // Для средних и больших шрифтов используем высокое качество
    return {
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high', // Высокое качество = плавное
    }
}

/**
 * Применяет оптимальные настройки рендеринга к контексту canvas
 * @param ctx - контекст 2D canvas
 * @param fontSize - размер шрифта текста на canvas (опционально, по умолчанию MIN_FONT_SIZE)
 */
export function applyOptimalRenderingSettings(
    ctx: CanvasRenderingContext2D,
    fontSize: number = MIN_FONT_SIZE
): void {
    const settings = getOptimalRenderingSettings(fontSize)
    ctx.imageSmoothingEnabled = settings.imageSmoothingEnabled
    ctx.imageSmoothingQuality = settings.imageSmoothingQuality
}

/**
 * Создание Fabric.js canvas
 * @param params - параметры для создания canvas
 * @returns экземпляр Canvas
 */
export function createCanvas(params: CanvasInitParams): Canvas {
    const {
        canvasElement,
        containerWidth,
        containerHeight,
        minWidth,
        minHeight,
        mediaDimensions,
        backgroundColor,
    } = params

    // Рассчитываем размеры канваса с учетом пропорций медиа и размеров контейнера
    const { width: canvasWidth, height: canvasHeight } =
        calculateCanvasDimensions({
            mediaWidth: mediaDimensions?.width,
            mediaHeight: mediaDimensions?.height,
            containerWidth,
            containerHeight,
            minWidth,
            minHeight,
        })

    // Создаем Fabric.js canvas
    const fabricCanvas = new Canvas(canvasElement, {
        width: canvasWidth,
        height: canvasHeight,
        backgroundColor: backgroundColor || CANVAS.BACKGROUND_COLOR,
        selection: false, // Отключаем выделение объектов по умолчанию
    })

    // Настраиваем качество рендеринга для лучшей четкости текста
    // Используем оптимальные настройки для маленьких шрифтов
    const ctx = canvasElement.getContext('2d')
    if (ctx) {
        // По умолчанию используем настройки для минимального размера шрифта
        // Настройки будут обновляться при изменении размера шрифта
        applyOptimalRenderingSettings(ctx, MIN_FONT_SIZE)
    }

    return fabricCanvas
}

/**
 * Очистка и удаление canvas
 * @param canvas - экземпляр Canvas для очистки
 */
export function disposeCanvas(canvas: Canvas | null): void {
    if (canvas) {
        canvas.dispose()
    }
}

/**
 * Параметры для обновления размеров canvas
 */
export interface UpdateCanvasDimensionsParams {
    canvas: Canvas
    containerWidth: number
    containerHeight: number
    minWidth: number
    minHeight: number
    mediaDimensions: MediaDimensions | null
}

/**
 * Обновление размеров canvas
 * @param params - параметры для обновления размеров
 * @returns новые размеры canvas
 */
export function updateCanvasDimensions(
    params: UpdateCanvasDimensionsParams
): { width: number; height: number } {
    const {
        canvas,
        containerWidth,
        containerHeight,
        minWidth,
        minHeight,
        mediaDimensions,
    } = params

    // Рассчитываем размеры канваса с учетом пропорций медиа и размеров контейнера
    const { width: canvasWidth, height: canvasHeight } =
        calculateCanvasDimensions({
            mediaWidth: mediaDimensions?.width,
            mediaHeight: mediaDimensions?.height,
            containerWidth,
            containerHeight,
            minWidth,
            minHeight,
        })

    // Обновляем размеры канваса
    canvas.setDimensions({
        width: canvasWidth,
        height: canvasHeight,
    })

    // Настраиваем качество рендеринга для лучшей четкости текста
    // Используем оптимальные настройки для маленьких шрифтов
    const canvasElement = canvas.getElement()
    if (canvasElement) {
        const ctx = canvasElement.getContext('2d')
        if (ctx) {
            // По умолчанию используем настройки для минимального размера шрифта
            // Настройки будут обновляться при изменении размера шрифта
            applyOptimalRenderingSettings(ctx, MIN_FONT_SIZE)
        }
    }

    return { width: canvasWidth, height: canvasHeight }
}


/**
 * Параметры для перерисовки canvas
 */
export interface RenderCanvasParams {
    canvas: Canvas
    textObj?: {
        setCoords: () => void
    }
}

/**
 * Перерисовка canvas
 * @param params - параметры для перерисовки
 */
export function renderCanvas(params: RenderCanvasParams): void {
    const { canvas, textObj } = params

    if (textObj) {
        textObj.setCoords()
    }

    canvas.renderAll()
}
